/**
 * This is a RecentlyViewedItem Component, which is used to display a single recently viewed item in the search list. This component incorporates an image and the Link.
 *
 * @module views/Molecules/RecentlyViewedItem
 * @memberof -Common
 */
import './RecentlyViewedItem.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Image from '@ulta/core/components/Image/Image';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import { handleRedirect } from '@ulta/core/utils/handleLocation/handleLocation';

import { TYPEAHEAD_SEARCH_TYPE } from '@ulta/modules/SearchHeader/SearchHeader';

/**
 * Represents a RecentlyViewedItem component
 *
 * @method
 * @param {RecentlyViewedItemProps} props - React properties passed from composition
 * @returns RecentlyViewedItem
 */

export const RecentlyViewedItem = function( props ){
  const {
    image,
    dataCaptureHandler,
    skuId
  } = props;

  const searchType = TYPEAHEAD_SEARCH_TYPE.RecentlyViewed;

  if( !image?.imageUrl ){
    return null;
  }

  return (
    <div className='RecentlyViewedItem'>
      <div className='RecentlyViewedItem__recentlyViewed'>
        <Link_Huge secondary
          action={ image?.action }
          onClick={ () => {
            dataCaptureHandler( { searchTerm: skuId, searchType, targetUrl: image?.action?.url } );
            handleRedirect( { url: image?.action?.url } );
          } }
        >
          <div className='RecentlyViewedItem__recentlyViewed__image'>
            <Image
              src={ image?.imageUrl }
              alt={ image?.altText }
              width={ 48 }
              height={ 48 }
            />
          </div>
        </Link_Huge>
      </div>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef RecentlyViewedItemProps
 * @property {object} image - The image details of the recently viewed item.
 * @property {string} image.imageUrl - The URL of the item's image.
 * @property {string} [image.altText] - The alternative text for the image.
 * @property {object} image.action - The navigation action associated with the image.
 * @property {string} image.action.url - The URL where the item navigates when clicked.
 * @property {string} skuId - The SKU ID associated with the recently viewed item.
 * @property {Function} dataCaptureHandler - The function to capture and store data capture events.
 */
export const propTypes = {
  /** Contains image details including the URL, alt text, and action */
  image: PropTypes.shape( {
    imageUrl: PropTypes.string,
    altText: PropTypes.string,
    action: PropTypes.shape( {
      url: PropTypes.string
    } )
  } ),
  /** SKU ID associated with the recently viewed item */
  skuId: PropTypes.string,
  /** Function to handle and capture data capture events */
  dataCaptureHandler: PropTypes.func
};

RecentlyViewedItem.propTypes = propTypes;

export default RecentlyViewedItem;
